import { Component, inject, OnInit, OnDestroy, signal, computed, effect } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { finalize, Subject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { PaginatorState } from 'primeng/paginator';
import { DynamicFilterConfig, DynamicFilterValues, EnumValues, FilterOption, createDynamicFilterState } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';

import { EnumService } from '../../../services/enum.service';
import { InventorySessionProxyService } from '../../../proxies/inventory/inventory-session.proxy.service';
import {
  InventorySession,
  InventorySessionStatus,
  InventorySessionSearchCommand,
  InventorySessionType,
} from '../../../models/inventory-session/inventory-session';
import { UserGroup } from '../../../models/authorization/enum/user-group.enum';

@Component({
  selector: 'app-inventory-sessions',
  templateUrl: './inventory-sessions.component.html',
  styleUrls: ['./inventory-sessions.component.scss'],
})
export class InventorySessionsComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly enumService = inject(EnumService);
  private readonly translateService = inject(TranslateService);
  private readonly proxyService = inject(InventorySessionProxyService);

  private readonly destroy$ = new Subject<void>();
  private appliedFilters: DynamicFilterValues = {};

  // State signals
  isLoading = signal<number>(0);
  showFilters = signal<boolean>(true);
  sessions = signal<InventorySession[]>([]);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'inventory-sessions-filters', config: this.filterConfig });

  // Pagination
  currentPage = 1;
  pageSize = 25;
  totalRecords = 0;

  // Enums
  inventorySessionStatus = signal<EnumValues>({});
  enumsReady = signal(false);

  // Computed: Status options for filter
  statusOptions = computed<FilterOption[]>(() => {
    const statuses = this.inventorySessionStatus();
    return Object.keys(statuses)
      .filter((key) => !isNaN(Number(key)))
      .map((key) => ({
        value: Number(key),
        label: statuses[Number(key)],
      }));
  });

  canCreate = computed(() => {
    return AuthService.hasRole(UserGroup.InventorySessionEditor) || AuthService.hasRole(UserGroup.InventorySessionAdmin);
  });

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters));

    effect(
      () => {
        const statusesValue = this.enumService.getEnum('InventorySessionStatus', 'inventory').values;
        if (Object.keys(statusesValue).length > 0) {
          this.inventorySessionStatus.set(statusesValue);
          this.enumsReady.set(true);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (this.translateService.currentLang && this.enumsReady()) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.tryInitializeFilterConfig();
    });
  }

  sessionStrict(session: unknown): InventorySession {
    return session as InventorySession;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private tryInitializeFilterConfig(): void {
    if (this.translateService.currentLang && this.enumsReady()) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'number',
          label: translate('InventorySession_Number', 'Number'),
          placeholder: translate('InventorySession_Enter_Number', 'Enter number'),
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'status',
          label: translate('InventorySession_Status', 'Status'),
          placeholder: translate('InventorySession_Select_Status', 'Select status'),
          options: this.statusOptions(),
          showClear: true,
          defaultVisible: true,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage = (event.page || 0) + 1;
    this.pageSize = event.rows || this.pageSize;
    this.search();
  }

  search(): void {
    const formValues = this.appliedFilters;

    const number = (formValues['number'] as string | undefined)?.trim();
    const statusRaw = formValues['status'] as InventorySessionStatus | { value?: InventorySessionStatus } | null | undefined;
    const status =
      statusRaw !== null && statusRaw !== undefined
        ? typeof statusRaw === 'object' && statusRaw.value !== undefined
          ? statusRaw.value
          : (statusRaw as InventorySessionStatus)
        : undefined;

    const command: InventorySessionSearchCommand = {
      number: number || undefined,
      statuses: status !== undefined ? [status] : undefined,
      type: InventorySessionType.Parent,
    };

    this.startLoading();
    this.proxyService
      .search(command, this.currentPage, this.pageSize)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (result) => {
          this.sessions.set(result?.data?.items || []);
          this.totalRecords = result?.data?.totalCount || 0;
        },
        error: () => {
          this.sessions.set([]);
          this.totalRecords = 0;
        },
      });
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    this.appliedFilters = filters;
    this.currentPage = 1;
    this.search();
  }

  redirectToDetail(id?: string): void {
    const queryParams = id ? { id } : { id: 'new' };
    this.router.navigate(['/inventory-session-details'], { queryParams });
  }

  getStatusLabel(status: InventorySessionStatus): string {
    const statuses = this.inventorySessionStatus();
    return statuses[status] || status.toString();
  }

  getStatusSeverity(status: InventorySessionStatus): 'success' | 'info' | 'warn' | 'secondary' | 'contrast' | 'danger' | undefined {
    switch (status) {
      case InventorySessionStatus.Open:
        return 'info';
      case InventorySessionStatus.Closed:
        return 'secondary';
      default:
        return 'secondary';
    }
  }

  formatDate(dateString?: string): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch {
      return dateString;
    }
  }
}
